import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { TextField } from '@mui/material'
import styles from './userInput.module.scss'
import globalStyles from '@/styles/components.module.scss'
import validator from 'validator'
import { Visibility, VisibilityOff } from '@mui/icons-material'

interface UserInputProps {
    id?: string
    label?: string
    type?: 'text' | 'password' | 'number' | 'phone' | 'email'
    fullWidth?: boolean
    required?: boolean
    parent?: boolean
    className?: string
    value?: string
    setValue?: (value: string) => void
    verify?: 'email' | 'password' | 'confirmPassword' | 'phone' | 'autoLink' | 'number' | 'textLength'
    textLength?: { min?: number; max?: number }
    setValid?: (error: boolean) => void
    confirmPasswordValue?: string
    disabled?: boolean
    format?: 'pascal' | 'upper' | 'lower' | 'num' | 'none' | 'phone' | 'zipCode' | 'float'
    unallowedCharacters?: 'alpha' | 'num' | 'special' | 'none' | 'email'
    readOnly?: boolean
    maxNumber?: number
    minNumber?: number
    endAdornment?: string
    onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void
    step?: number
    validationDelay?: number
    replaceAfterIdle?: number
    externalError?: boolean
}

const userInput: FunctionComponent<UserInputProps> = ({
    id,
    label,
    type = 'text',
    fullWidth,
    required,
    parent,
    className,
    value,
    setValue,
    verify,
    textLength,
    setValid,
    confirmPasswordValue,
    disabled,
    format,
    unallowedCharacters,
    readOnly,
    minNumber,
    maxNumber,
    endAdornment,
    onKeyPress,
    step = 1,
    validationDelay = 0,
    replaceAfterIdle = 0,
    externalError = false
}: UserInputProps) => {
    const [error, setError] = useState<boolean>(false)
    const [errorText, setErrorText] = useState<string>('')

    const [showPassword, setShowPassword] = useState<boolean>(false)

    const [draftValue, setDraftValue] = useState<string | null>(null)
    const pendingValue: React.RefObject<string | null> = useRef<string | null>(null)
    const validationTimer: React.RefObject<ReturnType<typeof setTimeout> | null> = useRef<ReturnType<
        typeof setTimeout
    > | null>(null)
    const lastInputAt: React.RefObject<number | null> = useRef<number | null>(null)
    const typedDigit: React.RefObject<string | null> = useRef<string | null>(null)
    const commitRef: React.RefObject<(value: string) => void> = useRef(commitValue)

    useEffect(() => {
        commitRef.current = commitValue
    })

    useEffect(() => {
        cancelValidation()
        pendingValue.current = null
        setDraftValue(null)
        return cancelValidation
    }, [value])

    function cancelValidation(): void {
        if (validationTimer.current !== null) {
            clearTimeout(validationTimer.current)
            validationTimer.current = null
        }
    }

    function flushValidation(): void {
        cancelValidation()
        if (pendingValue.current !== null) {
            const nextValue: string = pendingValue.current
            pendingValue.current = null
            setDraftValue(null)
            commitRef.current(nextValue)
        }
    }

    function onChange(e: React.ChangeEvent<HTMLInputElement>): void {
        let nextValue: string = e.target.value
        const nativeEvent: InputEvent = e.nativeEvent as InputEvent
        const digit: string | null = nativeEvent.data ?? typedDigit.current
        typedDigit.current = null
        const now: number = Date.now()
        if (
            type === 'number' &&
            replaceAfterIdle > 0 &&
            !nativeEvent.isComposing &&
            digit !== null &&
            /^\d$/.test(digit) &&
            (lastInputAt.current === null || now - lastInputAt.current >= replaceAfterIdle)
        ) {
            nextValue = digit
        }
        lastInputAt.current = now
        cancelValidation()
        if (validationDelay > 0) {
            pendingValue.current = nextValue
            setDraftValue(nextValue)
            validationTimer.current = setTimeout(flushValidation, validationDelay)
        } else {
            commitValue(nextValue)
        }
    }

    function commitValue(inputValue: string): void {
        let rawValue: string = inputValue

        // Nettoyage des caractères interdits
        if (type !== 'number' && unallowedCharacters) {
            switch (unallowedCharacters) {
                case 'alpha':
                    rawValue = rawValue.replace(/[^0-9+.,-]/g, '')
                    break
                case 'num':
                    rawValue = rawValue.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ \-'.]/g, '')
                    break
                case 'special':
                    rawValue = rawValue.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ \-'.0-9]/g, '')
                    break
                case 'email':
                    rawValue = rawValue.replace(/\s+/g, '')
                    break
            }
        }

        // Normalisation float
        const normalizedValue: string = rawValue.replace(',', '.')

        // Vérification
        if (verify) {
            verifyInput(normalizedValue)
        }

        // Gestion des nombres (int + float)
        if (type === 'number') {
            const numericValue: number = parseFloat(normalizedValue)

            if (!Number.isNaN(numericValue)) {
                if (maxNumber !== undefined && numericValue > maxNumber) {
                    rawValue = maxNumber.toString()
                }
                if (minNumber !== undefined && numericValue < minNumber) {
                    rawValue = minNumber.toString()
                }
            } else if (validationDelay > 0) {
                rawValue = value ?? ''
            }
        }

        // Format final
        if (setValue) {
            setValue(format ? formatValue(rawValue) : rawValue)
        }
    }

    function verifyInput(value: string): void {
        switch (verify) {
            case 'email':
                if (!validator.isEmail(value)) {
                    setNewError('Adresse email invalide')
                } else {
                    unsetError()
                }
                break
            case 'password':
                if (
                    !validator.isStrongPassword(value, {
                        minLength: 8,
                        minLowercase: 0,
                        minUppercase: 0,
                        minNumbers: 0,
                        minSymbols: 0
                    })
                ) {
                    setNewError('Mot de passe invalide, 8 caractères minimum')
                } else {
                    unsetError()
                }
                break
            case 'confirmPassword':
                if (value !== confirmPasswordValue) {
                    setNewError('Les mots de passe ne correspondent pas')
                } else {
                    unsetError()
                }
                break
            case 'phone':
                if (!validator.isMobilePhone(value)) {
                    setNewError('Numéro de téléphone invalide')
                } else {
                    unsetError()
                }
                break
            case 'autoLink':
                if (!validator.isURL(value)) {
                    setNewError('URL invalide')
                } else {
                    unsetError()
                }
                break
            case 'number':
                if (minNumber !== undefined && maxNumber !== undefined) {
                    if (!validator.isFloat(value, { min: minNumber, max: maxNumber })) {
                        setNewError(`Veuillez entrer un nombre entre ${minNumber} et ${maxNumber}`)
                    } else {
                        unsetError()
                    }
                } else if (minNumber !== undefined) {
                    if (!validator.isFloat(value, { min: minNumber })) {
                        setNewError(`Veuillez entrer un nombre supérieur à ${minNumber}`)
                    } else {
                        unsetError()
                    }
                } else if (maxNumber !== undefined) {
                    if (!validator.isFloat(value, { max: maxNumber })) {
                        setNewError(`Veuillez entrer un nombre inférieur à ${maxNumber}`)
                    } else {
                        unsetError()
                    }
                }
                break
            case 'textLength':
                if (validator.isLength(value, textLength)) {
                    unsetError()
                } else {
                    setNewError(
                        'Veuillez entrer un texte entre ' +
                            (textLength?.min || 0) +
                            ' et ' +
                            (textLength?.max || 256) +
                            ' caractères'
                    )
                }
        }
    }

    function formatValue(value: string): string {
        switch (format) {
            case 'pascal':
                return value.replace(/(^|\s)\S/g, function (l) {
                    return l.toUpperCase()
                })
            case 'upper':
                return value.toUpperCase()
            case 'lower':
                return value.toLowerCase()
            case 'num':
                return value.replace(/\D/g, '')
            case 'zipCode':
                return value.replace(/\D/g, '').slice(0, 5)
            case 'phone':
                return value.replace(/\D/g, '').slice(0, 10)
            case 'float':
                return value.replace(/[^0-9.,-]/g, '').replace(',', '.')
            default:
                return value
        }
    }

    function setNewError(text: string): void {
        setError(true)
        setErrorText(text)
        if (setValid) {
            setValid(false)
        }
    }

    function unsetError(): void {
        setError(false)
        setErrorText('')
        if (setValid) {
            setValid(true)
        }
    }

    return (
        <TextField
            id={id}
            label={label}
            fullWidth={fullWidth}
            required={required}
            variant='outlined'
            type={type === 'password' ? (showPassword ? 'text' : 'password') : type}
            error={error || externalError}
            helperText={errorText}
            className={`${parent ? styles.textFieldParent : styles.textFieldDaycare} ${className} ${error || externalError ? globalStyles.FieldInError : ''} ${readOnly ? styles.readOnly : ''} ${type === 'phone' || type === 'email' ? styles.hideInput : ''} ${type === 'number' && replaceAfterIdle > 0 ? styles.replaceAfterIdle : ''}`}
            value={(type === 'phone' || type === 'email') && value ? '' : (draftValue ?? value)}
            onChange={onChange}
            disabled={disabled || readOnly}
            onBlur={() => {
                flushValidation()
                lastInputAt.current = null
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                onKeyPress?.(e)
                typedDigit.current =
                    !e.defaultPrevented &&
                    !e.ctrlKey &&
                    !e.metaKey &&
                    !e.altKey &&
                    !e.nativeEvent.isComposing &&
                    /^\d$/.test(e.key)
                        ? e.key
                        : null
                if (!e.defaultPrevented && e.key === 'Enter') flushValidation()
            }}
            slotProps={{
                htmlInput: {
                    step: step
                },
                input: {
                    startAdornment: (type === 'phone' || type === 'email') && (
                        <a href={`${type === 'phone' ? 'tel:' : 'mailto:'}${value}`} className={styles.link}>
                            <span>{type === 'phone' ? addSpaceToPhoneNumber(value) : value}</span>
                        </a>
                    ),
                    endAdornment:
                        type === 'password' && value ? (
                            showPassword ? (
                                <VisibilityOff
                                    className={styles.IconEndAdornment}
                                    onClick={() => setShowPassword(false)}
                                />
                            ) : (
                                <Visibility className={styles.IconEndAdornment} onClick={() => setShowPassword(true)} />
                            )
                        ) : (
                            <span
                                className={
                                    endAdornment
                                        ? disabled
                                            ? styles.disabledEndAdornment
                                            : styles.endAdornment
                                        : styles.NoEndAdornment
                                }
                            >
                                {endAdornment}
                            </span>
                        )
                }
            }}
        />
    )
}

export default userInput

function addSpaceToPhoneNumber(phoneNumber?: string): string {
    if (!phoneNumber) return ''

    if (phoneNumber.length === 9) {
        return phoneNumber.replace(/(\d{3})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4')
    }
    return phoneNumber.replace(/(\d{2})(?=\d)/g, '$1 ')
}
